import React from "react";
import { Box, Divider, Typography, useTheme } from "@mui/material";
import { useLanguage } from "@tlc/ls2pac/components/Language";
import { HostSystemItemView } from "@tlc/ls2pac/dto/HostSystemItemView";
import { FineLineItem } from "./FineReceiptLineItem";
import { FinesReceiptRow } from "./FinesReceiptRow";
import { ConfirmDialog } from "@tlc/ui/dialog/ConfirmDialog";
import { useIsMobile } from "@tlc/ui/useIsMobile";

export interface FinesPaymentResponse {
  accountNumber: string;
  accountType: string;
  authorization: string;
  billTo: {
    address: string;
    city: string;
    country: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    state: string;
    zip: string;
  };
  dateTime: string;
  merchantName: string;
  orderDescription: string;
  orderInvoiceNumber: string;
  responseCode: string;
  totalAmount: string;
  transId: string;
}

export interface FinesPaymentReceiptDialogProps {
  dialogProps: {
    open: boolean;
    onClose: () => void;
    onTransitionExited: () => void;
  };
  fines: HostSystemItemView[];
  receipt: FinesPaymentResponse | null;
  hasPaymentFinishError: boolean;
}

// Adjusts UTC to user's local time
const formatReceiptDate = (authNetDateStr: string | undefined) => {
  if (!authNetDateStr) return "-";

  try {
    const utcDate = new Date(`${authNetDateStr} UTC`);

    const dateString = utcDate.toLocaleString(undefined, {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    if (dateString === "Invalid Date") {
      console.error("Error parsing date, invalid format:", authNetDateStr);
      throw new Error("Invalid date format");
    }

    return dateString;
  } catch (error) {
    // fallback in case Authorize.Net changes their string format
    return authNetDateStr;
  }
};

export const FinesPaymentReceiptDialog: React.FC<
  FinesPaymentReceiptDialogProps
> = ({ dialogProps, fines, receipt, hasPaymentFinishError }) => {
  const { translate } = useLanguage();
  const isMobile = useIsMobile();
  const theme = useTheme();

  if (!receipt) return null;
  return (
    <ConfirmDialog
      id="fines-receipt-dialog"
      maxWidth="sm"
      fullScreen={isMobile}
      fullWidth
      headline={translate("finesPaymentThankYou")}
      useSingleAction
      confirmButtonText={translate("close")}
      onConfirm={dialogProps.onClose}
      confirmButtonProps={{ disableElevation: true }}
      {...dialogProps}
    >
      {hasPaymentFinishError ? (
        <Typography color={theme.palette.error.dark}>
          {translate("finesPaymentSuccessErrorUpdating")}
        </Typography>
      ) : (
        <Typography fontWeight={600} fontSize="1rem">
          {translate("finesPaymentSuccess")}
        </Typography>
      )}
      <Box sx={{ mt: 1 }}>
        <Typography
          variant="subtitle1"
          fontWeight={700}
          sx={{ mb: 0.5, lineHeight: 1.2 }}
          color="primary"
        >
          {translate("orderInformation")}
        </Typography>
        <FinesReceiptRow
          label={`${translate("invoiceNumber")}:`}
          value={receipt.orderInvoiceNumber}
        />
        {receipt.merchantName && (
          <FinesReceiptRow
            label={`${translate("merchant")}:`}
            value={receipt.merchantName}
          />
        )}
        <FinesReceiptRow
          label={`${translate("description")}:`}
          value={receipt.orderDescription}
        />
        <FinesReceiptRow
          label={`${translate("dateTime")}:`}
          value={formatReceiptDate(receipt.dateTime)}
        />
      </Box>
      <Divider sx={{ my: 1.5 }} />
      <Box>
        <Typography
          variant="subtitle1"
          fontWeight={700}
          sx={{ mb: 0.5, lineHeight: 1.2 }}
          color="primary"
        >
          {translate("billingInformation")}
        </Typography>
        <Box>
          <Typography variant="body1" sx={{ lineHeight: 1.2 }}>
            {receipt.billTo.firstName} {receipt.billTo.lastName}
            <br />
            {receipt.billTo.address}
            <br />
            {receipt.billTo.city}, {receipt.billTo.state} {receipt.billTo.zip}
            <br />
            {receipt.billTo.phoneNumber}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 1.5 }} />

      <Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "auto 1fr auto",
            columnGap: 2,
            alignItems: "start",
            mb: 1,
          }}
        >
          <Box sx={{ minWidth: 6 }}>
            <Typography fontWeight={600} lineHeight={1}>
              {translate("item")}
            </Typography>
          </Box>
          <Box>
            <Typography fontWeight={600} lineHeight={1}>
              {translate("description")}
            </Typography>
          </Box>
          <Box>
            <Typography fontWeight={600} lineHeight={1}>
              {translate("amount")}
            </Typography>
          </Box>
        </Box>
        {fines.map((fine) => (
          <FineLineItem fine={fine} key={fine.id} />
        ))}
      </Box>
      <Divider />
      <Box>
        <Typography textAlign="right" fontWeight={600}>
          {translate("total")} {translate("amount")}:{" "}
          {`$${parseFloat(receipt.totalAmount).toFixed(2)}`}
        </Typography>

        <FinesReceiptRow
          label={`${translate("paymentMethod")}:`}
          value={`${receipt.accountType} ${receipt.accountNumber}`}
          labelTextAlign="right"
        />
        <FinesReceiptRow
          label={`${translate("transactionId")}:`}
          value={receipt.transId}
          labelTextAlign="right"
        />
        <FinesReceiptRow
          label={`${translate("authorization")}:`}
          value={receipt.authorization}
          labelTextAlign="right"
        />
      </Box>
    </ConfirmDialog>
  );
};
